/**
 * FeeStatusPill — standardized, reusable vendor-facing fee-status display.
 *
 * States (derived from /api/vendors/me/fee-status response):
 *   • Active waiver (> 7 days)    → soft green pill
 *   • Expiring soon (≤ 7 days)    → soft amber pill (NOT alert red)
 *   • No waiver / standard fee    → renders NOTHING by default
 *                                   OR a subtle neutral line if `fallback="line"`
 *                                   OR a neutral card if `variant="card"`
 *
 * Variants:
 *   • variant="pill"  (default)   — compact inline pill
 *   • variant="badge"             — most discreet form, used on the Vendor
 *                                   Dashboard greeting area. Tiny chip with
 *                                   icon + short label, taps through to the
 *                                   Payments tab where the canonical card
 *                                   lives. Renders nothing if no waiver.
 *   • variant="card"              — full-width card used ONLY on the Payments page
 *                                   (canonical reference surface, always renders)
 *
 * Props:
 *   feeStatus  — response object from /api/vendors/me/fee-status (or null)
 *   variant    — "pill" | "card"
 *   fallback   — "none" (default) | "line" — how to render the no-waiver state
 *                for pill variant. Card variant always renders a neutral card.
 *
 * NO banners. NO alert colors. Presentation only.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Clock, Info } from 'lucide-react';

const EXPIRING_SOON_THRESHOLD_DAYS = 7;

const SOURCE_LABELS = {
  referral: 'Referral reward',
  founding_promo: 'Founding promo',
  admin_waiver: 'Promotional credit',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const FeeStatusPill = ({ feeStatus, variant = 'pill', fallback = 'none' }) => {
  const hasWaiver = !!(feeStatus && feeStatus.is_promo && feeStatus.promo_end_date);
  const days = hasWaiver && typeof feeStatus.days_remaining === 'number'
    ? feeStatus.days_remaining
    : null;
  const expiringSoon = hasWaiver && days !== null && days >= 0 && days <= EXPIRING_SOON_THRESHOLD_DAYS;
  const sourceLabel = hasWaiver ? (SOURCE_LABELS[feeStatus.source] || null) : null;
  const endText = hasWaiver ? fmtDate(feeStatus.promo_end_date) : '';

  // ─────────── CARD VARIANT (Payments page — canonical, always renders) ───────────
  if (variant === 'card') {
    if (hasWaiver && !expiringSoon) {
      return (
        <div
          data-testid="fee-status-card"
          data-state="active"
          className="rounded-2xl p-4 border bg-gradient-to-br from-emerald-50/90 to-emerald-50/40 border-emerald-100"
        >
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-white shadow-sm border border-emerald-100/80 flex items-center justify-center shrink-0">
              <Award className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-emerald-700/60 font-semibold uppercase tracking-wider">
                Platform Fee
              </p>
              <p className="text-[15px] font-bold text-emerald-900 leading-tight mt-0.5">
                0% <span className="text-emerald-700/70 font-medium">·</span> {sourceLabel || 'Fee waiver active'}
              </p>
            </div>
            <div className="shrink-0 text-right border-l border-emerald-100 pl-4">
              <p className="text-[10px] text-emerald-700/60 font-semibold uppercase tracking-wider">
                Until
              </p>
              <p className="text-[15px] font-bold text-emerald-900 leading-tight mt-0.5">
                {endText}
              </p>
              {days !== null && (
                <p className="text-[11px] text-emerald-700/60" data-testid="fee-status-card-days">
                  {days} day{days === 1 ? '' : 's'} left
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (expiringSoon) {
      return (
        <div
          data-testid="fee-status-card"
          data-state="expiring_soon"
          className="rounded-2xl p-4 border bg-gradient-to-br from-amber-50/90 to-amber-50/40 border-amber-100"
        >
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-white shadow-sm border border-amber-100/80 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-amber-700/60 font-semibold uppercase tracking-wider">
                Platform Fee
              </p>
              <p className="text-[15px] font-bold text-amber-900 leading-tight mt-0.5">
                Fees resume in {days} day{days === 1 ? '' : 's'}
              </p>
              {sourceLabel && (
                <p className="text-[11px] text-amber-700/70 mt-0.5">
                  {sourceLabel} ends {endText}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    // No waiver — neutral detailed card (Payments page always shows a state)
    return (
      <div
        data-testid="fee-status-card"
        data-state="standard"
        className="rounded-2xl p-4 border bg-gradient-to-br from-gray-50/90 to-gray-50/40 border-gray-100"
      >
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">
              Platform Fee
            </p>
            <p className="text-[15px] font-bold text-gray-800 leading-tight mt-0.5">
              Standard pricing active
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Refer providers to earn fee-free time
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─────────── PILL VARIANT (Dashboard / Profile) — TRUE premium pill ───────────
  if (hasWaiver && !expiringSoon) {
    return (
      <div
        data-testid="vendor-fee-status-pill"
        data-state="active"
        className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-gradient-to-r from-emerald-50 to-emerald-50/60 border border-emerald-200/60 shadow-sm"
      >
        <div className="h-6 w-6 rounded-full bg-white border border-emerald-100/80 flex items-center justify-center shrink-0">
          <Award className="w-3 h-3 text-emerald-600" />
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 min-w-0 leading-tight">
          <span className="text-[12px] font-semibold text-emerald-800">
            No platform fees until {endText}
          </span>
          {sourceLabel && (
            <>
              <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-emerald-300 shrink-0" />
              <span className="text-[11px] text-emerald-700/70">{sourceLabel}</span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (expiringSoon) {
    return (
      <div
        data-testid="vendor-fee-status-pill"
        data-state="expiring_soon"
        className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-gradient-to-r from-amber-50 to-amber-50/60 border border-amber-200/60 shadow-sm"
      >
        <div className="h-6 w-6 rounded-full bg-white border border-amber-100/80 flex items-center justify-center shrink-0">
          <Clock className="w-3 h-3 text-amber-600" />
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 min-w-0 leading-tight">
          <span className="text-[12px] font-semibold text-amber-800">
            Fees resume in {days} day{days === 1 ? '' : 's'}
          </span>
          {sourceLabel && (
            <>
              <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-amber-300 shrink-0" />
              <span className="text-[11px] text-amber-700/70">{sourceLabel}</span>
            </>
          )}
        </div>
      </div>
    );
  }

  // No waiver — pill variant
  if (fallback === 'line') {
    return (
      <p
        data-testid="fee-status-line"
        data-state="standard"
        className="text-[11px] text-gray-500"
      >
        Standard pricing applies
      </p>
    );
  }
  return null;
};

// ──────────────────────────────────────────────────────────────────────────────
// BADGE VARIANT — the most discreet form. Used on the Vendor Dashboard
// greeting area where we want fee/referral context to be present but
// quiet, never reading like a promotional banner. Renders nothing when
// no waiver is active; on the Dashboard surface that's the desired
// "fee-silent" state.
// ──────────────────────────────────────────────────────────────────────────────
const FeeStatusPillBadge = ({ feeStatus }) => {
  const hasWaiver = !!(feeStatus && feeStatus.is_promo && feeStatus.promo_end_date);
  if (!hasWaiver) return null;

  const days = typeof feeStatus.days_remaining === 'number' ? feeStatus.days_remaining : null;
  const expiringSoon = days !== null && days >= 0 && days <= EXPIRING_SOON_THRESHOLD_DAYS;
  const endText = fmtDate(feeStatus.promo_end_date);

  // expiring branch — switch to neutral amber tint, still compact
  const tint = expiringSoon
    ? 'border-amber-200/70 text-amber-800 hover:bg-amber-50/40'
    : 'border-emerald-200/70 text-emerald-800 hover:bg-emerald-50/40';
  const Icon = expiringSoon ? Clock : Award;
  const iconColor = expiringSoon ? 'text-amber-600' : 'text-emerald-600';
  const label = expiringSoon
    ? `${days}d`
    : (endText ? `Free until ${endText}` : 'Fee waiver active');

  return (
    <Link
      to="/vendor/dashboard?tab=payments"
      data-testid="vendor-fee-status-pill"
      data-variant="badge"
      data-state={expiringSoon ? 'expiring_soon' : 'active'}
      aria-label={
        expiringSoon
          ? `Platform fee waiver expires in ${days} days`
          : `No platform fees until ${endText}`
      }
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border ${tint} text-[11px] font-medium transition-colors`}
    >
      <Icon className={`w-3 h-3 ${iconColor}`} aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
};

const FeeStatusPillWithBadge = (props) => {
  if (props.variant === 'badge') {
    return <FeeStatusPillBadge feeStatus={props.feeStatus} />;
  }
  return <FeeStatusPill {...props} />;
};

export default FeeStatusPillWithBadge;
